import get from 'lodash/get'
import { hasPermission } from '../services/permission'
import { unformatDSL } from './search'
import intl from 'react-intl-universal'
import moment from 'moment-timezone'

const dateLookup = ['created_date', 'publish_date', 'last_updated_date']

export const SORT_TYPE = Object.freeze({
  CREATED_DATE: {
    text: intl.get('searchFiltersService.sortFilters.createdDate'),
    value: 'created_date:desc',
  },
  LAST_UPDATED_DATE: {
    text: intl.get('searchFiltersService.sortFilters.lastUpdatedDate'),
    value: 'last_updated_date:desc',
  },
  PUBLISH_DATE: {
    text: intl.get('searchFiltersService.sortFilters.publishDate'),
    value: 'publish_date:desc',
  },
  DISPLAY_DATE: {
    text: intl.get('searchFiltersService.sortFilters.displayDate'),
    value: 'display_date:desc',
  },
  RELEVANCE: {
    text: intl.get('searchFiltersService.sortFilters.relevance'),
    value: '_score:desc',
  },
})

export const createSearchFilters = ({
  queryLookup = {},
  websiteOptions = {},
  sectionOptions = {},
  webskedOptions = [],
  applyDefaultSearchFilter,
  timezone,
  searchFilters = {},
  subtypeOptions = [],
}) => {
  const {
    searchBar = '',
    sortFilters,
    time,
    start,
    end,
    'credits.by.name': creditsByName,
    'headlines.basic': headlinesBasic,
    'source.system': sourceSystem,
    subtype,
    'revision.published': revisionPublished,
  } = queryLookup
  const { Author: author, Headline: headline, Source: source } = searchFilters
  const { value: sectionOptionsValue = '' } = sectionOptions
  const userHasAllStoriesPermission = hasPermission(
    window.userRolesAuth.story.allStories
  )
  const leftFilters = [
    {
      label: intl.get('searchFiltersService.leftFilters.typeLabel'),
      search: 'type',
      hidden: true,
      setSearch: function () {
        return this.inputs[0].value
      },
      inputs: [
        {
          type: 'hidden',
          value: 'story',
        },
      ],
    },
    {
      label: intl.get('searchFiltersService.leftFilters.timeLabel'),
      collapse: true,
      search: time || (applyDefaultSearchFilter ? 'created_date' : ''),
      setSearch: function () {
        const start =
          this.inputs.filter((i) => i.name === 'start')[0].value || ''
        const end = this.inputs.filter((i) => i.name === 'end')[0].value || ''
        const range = {}
        if (start) {
          range.gte = moment(start).utc().format()
        }

        if (end) {
          range.lte = moment(end).utc().format()
        }
        // https://www.elastic.co/guide/en/elasticsearch/reference/current/query-dsl-range-query.html
        return this.search ? range : ''
      },
      inputs: [
        {
          type: 'radio',
          label: intl.get('searchFiltersService.leftFilters.createdLabel'),
          value: 'created_date',
        },
        {
          type: 'radio',
          label: intl.get('searchFiltersService.leftFilters.publishedLabel'),
          value: 'publish_date',
        },
        {
          type: 'radio',
          label: intl.get('searchFiltersService.leftFilters.lastModifiedLabel'),
          value: 'last_updated_date',
        },
        {
          label: intl.get('searchFiltersService.leftFilters.startTimeLabel'),
          type: 'daterange',
          settings: {
            enableTime: true,
          },
          name: 'start',
          value:
            start ||
            (applyDefaultSearchFilter
              ? moment().tz(timezone).startOf('day')
              : null),
        },
        {
          label: intl.get('searchFiltersService.leftFilters.endTimeLabel'),
          type: 'daterange',
          settings: {
            enableTime: true,
          },
          name: 'end',
          value: end || null,
        },
      ],
    },
    {
      label: intl.get('searchFiltersService.leftFilters.siteLabel'),
      hidden: true,
      search: 'taxonomy.sections._id', // for ANS 0.6.0 this will have to change to taxonomy.sections._id
      setSearch: function () {
        return this.inputs[0].value
      },
      inputs: [
        {
          type: 'hidden',
          value: sectionOptionsValue,
        },
      ],
    },
    {
      label: intl.get('searchFiltersService.leftFilters.authorLabel'),
      collapse: false,
      search: 'credits.by.name',
      setSearch: function () {
        return this.inputs[0].value
      },
      inputs: [
        {
          label: intl.get('searchFiltersService.leftFilters.authorInputLabel'),
          type: 'text',
          placeholder: userHasAllStoriesPermission
            ? intl.get(
                'searchFiltersService.leftFilters.authorInputPlaceholder'
              )
            : localStorage.getItem('arc.displayName'),
          autocomplete: 'authorService',
          value: creditsByName || (typeof author === 'string' ? author : ''),
          dataTestId: 'author-name-search-filter',
          disabled: !userHasAllStoriesPermission,
        },
      ],
    },
    {
      label: intl.get('searchFiltersService.leftFilters.headlineLabel'),
      collapse: false,
      search: 'headlines.basic',
      setSearch: function () {
        return this.inputs[0].value
      },
      inputs: [
        {
          label: intl.get(
            'searchFiltersService.leftFilters.headlineInputLabel'
          ),
          type: 'text',
          placeholder: intl.get(
            'searchFiltersService.leftFilters.headlineInputPlaceholder'
          ),
          value:
            headlinesBasic || (typeof headline === 'string' ? headline : ''),
          dataTestId: 'headline-search-filter',
        },
      ],
    },
    {
      label: intl.get('searchFiltersService.leftFilters.sourceLabel'),
      collapse: false,
      search: 'source.system',
      setSearch: function () {
        return this.inputs[0].value
      },
      inputs: [
        {
          label: intl.get('searchFiltersService.leftFilters.sourceInputLabel'),
          type: 'text',
          placeholder: intl.get(
            'searchFiltersService.leftFilters.sourceInputPlaceholder',
            { appName: 'Composer' }
          ),
          default: source || '',
          value: sourceSystem || source || '',
          dataTestId: 'source-search-filter',
        },
      ],
    },
    {
      label: intl.get('searchFiltersService.leftFilters.subtypeLabel'),
      collapse: false,
      search: 'subtype',
      setSearch: function () {
        return this.inputs[0].value
      },
      inputs: [
        {
          label: intl.get('searchFiltersService.leftFilters.subtypeInputLabel'),
          type: 'select',
          options: Array.isArray(subtypeOptions) ? subtypeOptions : [],
          value: subtype || '',
        },
      ],
    },
    {
      label: intl.get('searchFiltersService.leftFilters.publishedLabel'),
      collapse: true,
      search: 'revision.published',
      setSearch: function () {
        return this.inputs[0].value ? 'true' : 'false'
      },
      inputs: [
        {
          label: intl.get(
            'searchFiltersService.leftFilters.publishedInputLabel'
          ),
          type: 'toggle',
          text: intl.get('searchFiltersService.leftFilters.publishedInputText'),
          default: false,
          value: ((param) => {
            if (!param) {
              return ''
            } else if (param === 'true') {
              return true
            } else {
              return false
            }
          })(revisionPublished),
        },
      ],
    },
  ]

  if (!!webskedOptions && webskedOptions.length > 0) {
    leftFilters.push({
      label: intl.get('searchFiltersService.leftFilters.workflowStateLabel'),
      collapse: true,
      search: 'workflow.status_code',
      dataTestId: 'workflow-search-filters',
      setSearch: function () {
        let search = []
        if (Array.isArray(this.inputs)) {
          search = this.inputs
            .filter((i) => i.checked === true)
            .map((i) => i.value)
        }
        return search.length && search.length !== this.inputs.length
          ? search
          : ''
      },
      inputs: webskedOptions,
    })
  }

  return {
    searchBar: {
      value: searchBar || '',
    },

    sortFilters: {
      value: sortFilters || 'created_date:desc',
      filters: [
        SORT_TYPE.CREATED_DATE,
        SORT_TYPE.LAST_UPDATED_DATE,
        SORT_TYPE.PUBLISH_DATE,
        SORT_TYPE.DISPLAY_DATE,
        SORT_TYPE.RELEVANCE,
      ],
    },

    websites: websiteOptions,

    sections: sectionOptions,

    leftFilters: leftFilters,
  }
}

export const createLookupTable = (arr = []) => {
  if (!Array.isArray(arr)) return {}
  return arr.reduce((table, i) => {
    if (dateLookup.includes(i.path)) {
      table.time = i.path
      table.start = i.value.gte
      table.end = i.value.lte
    } else {
      table[i.path] = i.value
    }
    return table
  }, {})
}

export const createWebsiteOptions = (
  options = {},
  queryLookup = {},
  toastr
) => {
  const { website: selectedWebsite = '' } = queryLookup
  const optionKeys =
    !!options && typeof options === 'object' ? Object.keys(options) : []
  const userPermissions = get(window, 'userRolesAuth.story.viewStory', {})
  const websiteOptions = {
    filters: [],
    value: '',
    defaultValue: '',
  }

  if (!optionKeys.length) {
    toastr.error(intl.get('searchFiltersService.selectWebsite.noSites'))
    return websiteOptions
  }

  websiteOptions.filters = optionKeys
    .filter((k) => {
      const website = options[k]
      const userHasPermissions = hasPermission(userPermissions, website._id)
      // set default website
      if (website.is_default_website && userHasPermissions) {
        websiteOptions.defaultValue = website._id
      }
      // set website selected value
      if (selectedWebsite === website._id && userHasPermissions) {
        websiteOptions.value = website._id
      }
      return userHasPermissions
    })
    .map((k) => ({ text: options[k].display_name, value: options[k]._id }))

  if (websiteOptions.filters.length === 0) {
    toastr.error(intl.get('searchFiltersService.selectWebsite.noAccess'))
    return websiteOptions
  }

  // check if there is no values and set them to the first list item
  if (!websiteOptions.defaultValue) {
    websiteOptions.defaultValue = websiteOptions.filters[0].value
  }
  if (!websiteOptions.value) {
    websiteOptions.value = websiteOptions.defaultValue
  }
  return websiteOptions
}

export const createSectionOptions = (sections = [], value = '') => {
  const sectionOptions = { filters: [], value: '' }
  if (!Array.isArray(sections)) return sectionOptions
  sectionOptions.filters = sections.map((i) => ({ text: i.name, value: i.id }))
  sectionOptions.value = value
  return sectionOptions
}

export const createWebskedOptions = (webskedOptions, checked = []) => {
  if (!Array.isArray(webskedOptions)) return []
  if (checked && checked.length > 0) {
    return webskedOptions.map((i) => {
      return {
        type: 'checkbox',
        value: i.id,
        label: i.name,
        checked: checked.includes(i.id),
      }
    })
  }
  return webskedOptions.map((i) => {
    return { type: 'checkbox', value: i.id, label: i.name, checked: true }
  })
}

export const createPicklistOptions = (picklist) => {
  return get(picklist, 'data.picklists.subtype.picklist', {})
}

export const createSubtypeOptions = (picklist) => {
  if (!Array.isArray(picklist) || picklist.length === 0) return []
  return [
    { name: '', value: '' },
    ...picklist
      .filter((i) => i.active)
      .map((i) => ({ name: i.text, value: i.id })),
  ]
}

export const createUrlLookupTable = (urlParams) => {
  const { body, sort = '', website = '' } = urlParams || {}
  const urlQuery = unformatDSL(body)
  if (sort && typeof sort === 'string') {
    urlQuery.push({ path: 'sortFilters', value: sort })
  }
  if (website && typeof website === 'string') {
    urlQuery.push({ path: 'website', value: website })
  }
  return createLookupTable(urlQuery)
}

export const useUrlQueryOrLocalstorage = (url, local) => {
  return Object.keys(url).length > 0 ? url : local
}

export const getFilters = (
  siteService,
  webskedService,
  settingsService,
  toastr,
  pharosService
) => async (
  urlQueryParams = {},
  scope = {},
  localStorageSearchSettings = {}
) => {
  try {
    const { getWebsites, getWebsite: getWebsiteSections } = siteService || {}
    const { getWebskedStatus } = webskedService || {}
    const { fetchPicklists, fetchSettings } = settingsService || {}
    const { picklist, companySettings } = scope || {}

    let websiteList
    try {
      websiteList = await getWebsites()
    } catch (error) {
      error.service = 'site'
      throw error
    }
    let picklists = {}
    try {
      picklists = await fetchPicklists()
    } catch (e) {
      console.error('fetchPicklists failed ', e)
    }
    const requestCompanySettings = async () => {
      try {
        return await fetchSettings('org')
      } catch (error) {
        error.service = 'settings'
        throw error
      }
    }
    const picklistOptions =
      get(picklist, 'picklists.subtype.picklists') ||
      createPicklistOptions(picklists)
    const {
      searchFilters = {},
      applyDefaultSearchFilter = false,
      disableWebskedIntegration = true,
      timezone = '',
    } = companySettings || requestCompanySettings()
    const localSearchSettings = createLookupTable(localStorageSearchSettings)
    const urlSearchQuery = createUrlLookupTable(urlQueryParams)
    const queryLookup = useUrlQueryOrLocalstorage(
      urlSearchQuery,
      localSearchSettings
    )

    const websiteOptions = createWebsiteOptions(
      websiteList,
      queryLookup,
      toastr
    )

    let sectionsList
    try {
      sectionsList = await getWebsiteSections(
        websiteOptions.value || websiteOptions.defaultValue
      )
    } catch (error) {
      error.service = 'site'
      throw error
    }

    const { sections = [] } = sectionsList

    const sectionOptions = createSectionOptions(
      sections,
      get(queryLookup, 'taxonomy.sections._id')
    )

    const subtypeOptions = createSubtypeOptions(picklistOptions)

    let webskedOptions = []
    if (!disableWebskedIntegration) {
      let webskedData = {
        data: [],
      }
      try {
        webskedData = await getWebskedStatus()
      } catch (e) {
        console.error('fetchWebskedStatus failed ', e)
      }
      webskedOptions = createWebskedOptions(
        webskedData.data,
        queryLookup['workflow.status_code']
      )
    }

    return createSearchFilters({
      queryLookup,
      websiteOptions,
      sectionOptions,
      webskedOptions,
      applyDefaultSearchFilter,
      timezone,
      searchFilters,
      subtypeOptions,
    })
  } catch (err) {
    // report general exceptions from getFilters
    if (!err.service) {
      pharosService.sendExceptionToSentry(err, err.message)
    }
    // otherwise just throw, site/settings services handle sentry reporting
    throw err
  }
}
